import "./ProjectorScroller.scss";

import { throttle } from "lodash-es";
import { FC, HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import {
  WindowFrameMessage,
  WindowMessages,
  addInstructorMessageListener,
  createScrollMessageWithBlockMarker,
  removeInstructorMessageListener,
  sendMessageToProjectorView,
} from "./projector-messaging";

import { PAGE_SCROLL_CONTAINER_ID } from "components/constants";
import { PageButtons } from "components/materials/page/buttons/PageButtons";
import { useAppDispatch } from "store/index";
import { viewStateActions } from "store/slices/view";
import { isLocalEnv } from "utils/env";
import {
  DEFAULT_SCALING_FACTOR,
  FeatureStatus,
  usePresentation,
  useProjectorScalingFactor,
} from "utils/presentation";

// Integration tests need 0.  Even using 1ms will not send in more than
// one scroll message and we need to scroll multiple times to get to the
// bottom of the page in the integration tests.
// eslint-disable-next-line react-refresh/only-export-components
export const SCROLL_DEBOUNCER_MS = isLocalEnv ? 0 : 100;

const BACK_TO_TOP_THRESHOLD = 300;

type ProjectorScrollerProps = HTMLAttributes<HTMLDivElement> & {
  shouldScroll?: boolean;
  shouldShowBackToTop?: boolean;
  isFeatureModal?: boolean;
  isInVirtualClass?: boolean;
  pageId?: string;
};

// Handles syncing the scroll position of a div to the div container we have
// in the projector view.  Note that the header content at the start of this div
// will differ from what we show in the projector view.  So we need to calculate
// the offset height of the header content to make sure we are scrolling to the
// same position as the projector view.  Note: we are currently not scaling
// the width as this complicates the layout with the sidebar.  We hope the content
// remains the same size as it should be 660px wide for instructor view and 20%
// more for the projector view.
export const ProjectorScroller: FC<ProjectorScrollerProps> = ({
  children,
  className,
  shouldScroll = true,
  shouldShowBackToTop = false,
  isFeatureModal = false,
  isInVirtualClass = false,
  pageId,
  id,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const scrollDivRef = useRef<HTMLDivElement | null>(null);
  const projectorFrameRef = useRef<WindowFrameMessage | null>(null);
  const scalingFactorRef = useRef(DEFAULT_SCALING_FACTOR);

  const shouldSyncScrollRef = useRef(false);
  const { featureStatus, isPresenting, hasProjectorView } = usePresentation();
  const isFeaturingRef = useRef(false);
  const isFeaturing = featureStatus !== FeatureStatus.NONE;
  isFeaturingRef.current = isFeaturing;
  const isInVirtualClassRef = useRef(false);
  isInVirtualClassRef.current = isInVirtualClass;

  const scalingFactor = useProjectorScalingFactor();
  scalingFactorRef.current = scalingFactor;

  const isProjecting = isPresenting && hasProjectorView;
  const shouldSyncScrolling = Boolean(shouldScroll && isProjecting);
  shouldSyncScrollRef.current = shouldSyncScrolling;

  const [showBackToTop, setShowBackToTop] = useState(false);
  const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false);

  // Needed for the divider in PageButtons
  useEffect(() => {
    const scrollDiv = scrollDivRef.current;
    if (!scrollDiv) return;

    const checkScrollbar = () => {
      if (!scrollDiv) return;

      const hasScrollbar = scrollDiv.scrollHeight > scrollDiv.clientHeight;
      setHasVerticalScrollbar(hasScrollbar);
    };

    const throttledCheckScrollbar = throttle(checkScrollbar, SCROLL_DEBOUNCER_MS, {
      trailing: true,
      leading: true,
    });

    // TODO: find a better way to wait for the page to load?
    const timeoutId = setTimeout(() => {
      checkScrollbar();
    }, 1000);

    const resizeObserver = new ResizeObserver(throttledCheckScrollbar);
    resizeObserver.observe(scrollDiv);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      throttledCheckScrollbar.cancel();
    };
  }, [pageId]);

  const postScrollMessage = useCallback(() => {
    if (!projectorFrameRef.current) {
      sendMessageToProjectorView({ type: WindowMessages.REQUEST_FRAME });
      return;
    }

    if (scrollDivRef.current) {
      // Send the scroll position to the projector with the scaling factor applied
      const scrollMessage = createScrollMessageWithBlockMarker(scrollDivRef.current);

      sendMessageToProjectorView(scrollMessage);
    }
  }, []);

  const handleScroll = useMemo(
    () =>
      throttle(postScrollMessage, SCROLL_DEBOUNCER_MS, {
        trailing: true,
        leading: true,
      }),
    [postScrollMessage],
  );

  const scrollBackToTop = () => {
    if (scrollDivRef.current) {
      scrollDivRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Need to reset scrolling after stopping featuring.  We use the isProjecting flag over the
  // shouldSyncScrolling flag here because if we are on the responses tab we aren't actually
  // syncing but we still want to reset the scroll position.
  useEffect(() => {
    if (!isFeaturing && isProjecting) {
      if (scrollDivRef.current) {
        postScrollMessage();
      } else {
        sendMessageToProjectorView({
          type: WindowMessages.PAGE_SCROLL,
          scrollX: 0,
          scrollY: 0,
        });
      }
    }
  }, [isFeaturing, isProjecting, postScrollMessage]);

  useEffect(() => {
    if (!scrollDivRef.current) {
      return;
    }

    const scrollingDiv = scrollDivRef.current;

    if (shouldSyncScrolling) {
      scrollingDiv.addEventListener("scroll", handleScroll);
    } else {
      // Remove any custom height we added so that it goes back to the browser default.
      scrollingDiv.style.flex = "1 1 auto";

      scrollingDiv.removeEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollingDiv) {
        scrollingDiv.removeEventListener("scroll", handleScroll);
      }
    };
  }, [shouldSyncScrolling, isPresenting, handleScroll]);

  // On page change just reset the scroll position
  useEffect(() => {
    scrollBackToTop();
  }, [pageId]);

  useEffect(() => {
    const handleFrameMessage = (message: MessageEvent<WindowFrameMessage>) => {
      if (message.data.type === WindowMessages.WINDOW_FRAME) {
        projectorFrameRef.current = message.data;

        dispatch(
          viewStateActions.setProjectorHeight(
            projectorFrameRef.current.viewport.height / scalingFactorRef.current,
          ),
        );

        if (projectorFrameRef.current.assetContainer) {
          dispatch(
            viewStateActions.setProjectorFeaturedAssetContainer({
              width: projectorFrameRef.current.assetContainer.width,
              height: projectorFrameRef.current.assetContainer.height,
              hasHorizontalScrollbar:
                projectorFrameRef.current.assetContainer.hasHorizontalScrollbar,
              hasVerticalScrollbar: projectorFrameRef.current.assetContainer.hasVerticalScrollbar,
            }),
          );
        }
      }
    };

    addInstructorMessageListener(handleFrameMessage);

    return () => {
      removeInstructorMessageListener(handleFrameMessage);
    };
  }, [dispatch]);

  const handleScrollForBackToTop = useMemo(
    () =>
      throttle(
        () => {
          setShowBackToTop(scrollDivRef.current?.scrollTop > BACK_TO_TOP_THRESHOLD);
        },
        SCROLL_DEBOUNCER_MS,
        {
          trailing: true,
          leading: true,
        },
      ),
    [],
  );

  useEffect(() => {
    if (!scrollDivRef.current) {
      return;
    }

    const scrollingDiv = scrollDivRef.current;

    if (shouldShowBackToTop) {
      scrollingDiv.addEventListener("scroll", handleScrollForBackToTop);
    } else {
      scrollingDiv.removeEventListener("scroll", handleScrollForBackToTop);
    }

    return () => {
      if (scrollingDiv) {
        scrollingDiv.removeEventListener("scroll", handleScrollForBackToTop);
      }
    };
  }, [handleScrollForBackToTop, shouldShowBackToTop]);

  return (
    <div
      // Note: we need the relative here to ensure our offset calculations can start
      // from this div when scrolling.
      className={twMerge("projector-scroller", className)}
      id={id || PAGE_SCROLL_CONTAINER_ID}
      ref={scrollDivRef}
      {...props}
    >
      {children}

      {!isFeatureModal && (
        <PageButtons
          containerHasVerticalScrollbar={hasVerticalScrollbar}
          pageId={pageId}
          scrollBackToTop={scrollBackToTop}
          showBackToTop={shouldShowBackToTop && showBackToTop}
        />
      )}
    </div>
  );
};
